const STORAGE_KEY = "afrimed.accessibility-fola";

export const DEFAULT_ACCESSIBILITY_FOLA_PREFS = {
  folaSummaries: true,
  folaExplainMeds: true,
  folaPrepQuestions: true,
  largeText: false,
  reduceMotion: false,
  highContrast: false,
};

function toBool(value, fallback = false) {
  return typeof value === "boolean" ? value : fallback;
}

export function normalizeAccessibilityFolaPrefs(raw) {
  const obj = raw && typeof raw === "object" ? raw : {};

  const largeText = toBool(
    obj.largeText,
    toBool(obj?.accessibility?.largeText, DEFAULT_ACCESSIBILITY_FOLA_PREFS.largeText)
  );
  const reduceMotion = toBool(
    obj.reduceMotion,
    toBool(obj?.accessibility?.reduceMotion, DEFAULT_ACCESSIBILITY_FOLA_PREFS.reduceMotion)
  );
  const highContrast = toBool(
    obj.highContrast,
    toBool(obj?.accessibility?.highContrast, DEFAULT_ACCESSIBILITY_FOLA_PREFS.highContrast)
  );

  const folaSummaries = toBool(
    obj.folaSummaries,
    toBool(obj?.fola?.summaries, DEFAULT_ACCESSIBILITY_FOLA_PREFS.folaSummaries)
  );
  const folaExplainMeds = toBool(
    obj.folaExplainMeds,
    toBool(obj?.fola?.explainMeds, DEFAULT_ACCESSIBILITY_FOLA_PREFS.folaExplainMeds)
  );
  const folaPrepQuestions = toBool(
    obj.folaPrepQuestions,
    toBool(obj?.fola?.prepQuestions, DEFAULT_ACCESSIBILITY_FOLA_PREFS.folaPrepQuestions)
  );

  return {
    largeText,
    reduceMotion,
    highContrast,
    folaSummaries,
    folaExplainMeds,
    folaPrepQuestions,
  };
}

export function mapAccessibilityFolaPrefsToDb(prefs) {
  const normalized = normalizeAccessibilityFolaPrefs(prefs);

  return {
    ...normalized,
    accessibility: {
      largeText: normalized.largeText,
      reduceMotion: normalized.reduceMotion,
      highContrast: normalized.highContrast,
    },
    fola: {
      summaries: normalized.folaSummaries,
      explainMeds: normalized.folaExplainMeds,
      prepQuestions: normalized.folaPrepQuestions,
    },
    updated_at: new Date().toISOString(),
  };
}

export function readAccessibilityFolaPrefs() {
  if (typeof window === "undefined") {
    return DEFAULT_ACCESSIBILITY_FOLA_PREFS;
  }

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_ACCESSIBILITY_FOLA_PREFS;
    return normalizeAccessibilityFolaPrefs(JSON.parse(raw));
  } catch {
    return DEFAULT_ACCESSIBILITY_FOLA_PREFS;
  }
}

export function writeAccessibilityFolaPrefs(prefs) {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify(normalizeAccessibilityFolaPrefs(prefs))
    );
  } catch {
    // ignore local storage failures
  }
}

export function applyAccessibilityFolaPrefs(prefs) {
  if (typeof document === "undefined") return;

  const root = document.documentElement;
  const normalized = normalizeAccessibilityFolaPrefs(prefs);

  root.setAttribute(
    "data-afrimed-text-scale",
    normalized.largeText ? "large" : "default"
  );
  root.setAttribute(
    "data-afrimed-motion",
    normalized.reduceMotion ? "reduced" : "default"
  );
  root.setAttribute(
    "data-afrimed-contrast",
    normalized.highContrast ? "high" : "default"
  );

  try {
    window.dispatchEvent(
      new CustomEvent("afrimed-accessibility-change", {
        detail: normalized,
      })
    );
  } catch {
    // ignore event dispatch failures
  }
}

export function initAccessibilityFolaPrefs() {
  applyAccessibilityFolaPrefs(readAccessibilityFolaPrefs());
}

export function getAccessibilityFolaStorageKey() {
  return STORAGE_KEY;
}
